import React from "react";
import ReactDOM from "react-dom/client";
import "./localization/i18n";
import "@fortawesome/fontawesome-free/css/all.min.css";
import "./index.css";
import "@aws-amplify/ui-react/styles.css";
import "./amplifyConfig";
import App from "./App";

// Suppress browser extension connection errors
// const originalConsoleError = console.error;
// console.error = (...args) => {
//   // Filter out various browser extension connection errors
//   const errorMessage = args.join(" ");

//   // Suppress specific browser extension errors
//   const suppressedErrors = [
//     "Could not establish connection. Receiving end does not exist",
//     "Extension context invalidated",
//     "The message port closed before a response was received",
//     "Cannot read properties of null (reading 'postMessage')",
//     "Cannot read properties of undefined (reading 'postMessage')",
//     "Failed to fetch",
//     "NetworkError when attempting to fetch resource",
//     // Google Analytics and third-party script errors
//     "Script error",
//     "Loading chunk",
//     "Loading CSS chunk",
//     "gtag",
//     "googletagmanager",
//     "analytics",
//     "chrome-extension://",
//     "moz-extension://",
//     "safari-extension://",
//     "Uncaught (in promise)",
//     "Promise rejection",
//     // Common browser extension error patterns
//     "connection was closed",
//     "connection is closed",
//     "port is closed",
//     "disconnected port",
//     "invalidated context",
//     "context was invalidated",
//   ];

//   // Check if any of the suppressed error patterns match
//   const shouldSuppress = suppressedErrors.some((errorPattern) =>
//     errorMessage.toLowerCase().includes(errorPattern.toLowerCase())
//   );

//   if (shouldSuppress) {
//     // Only log in development mode for debugging
//     if (process.env.NODE_ENV === "development") {
//       console.debug(
//         "🔧 Suppressed browser extension/third-party error:",
//         errorMessage
//       );
//     }
//     return; // Suppress this error
//   }

//   // Call the original console.error for other errors
//   originalConsoleError.apply(console, args);
// };

// // Also suppress unhandled promise rejections from extensions
// const originalConsoleWarn = console.warn;
// console.warn = (...args) => {
//   const warningMessage = args.join(" ");

//   // Suppress extension-related warnings
//   const suppressedWarnings = [
//     "Could not establish connection",
//     "Extension context",
//     "Receiving end does not exist",
//   ];

//   const shouldSuppress = suppressedWarnings.some((warningPattern) =>
//     warningMessage.includes(warningPattern)
//   );

//   if (shouldSuppress) {
//     return; // Suppress this warning
//   }

//   // Call the original console.warn for other warnings
//   originalConsoleWarn.apply(console, args);
// };

// Suppress unhandled promise rejections
window.addEventListener("unhandledrejection", (event) => {
  const errorMessage = event.reason?.message || event.reason || "";
  const errorString =
    typeof event.reason === "string"
      ? event.reason
      : JSON.stringify(event.reason);

  const suppressedPromiseErrors = [
    "Could not establish connection. Receiving end does not exist",
    "Extension context invalidated",
    "The message port closed",
    "connection was closed",
    "connection is closed",
    "port is closed",
    "disconnected port",
    "invalidated context",
    "context was invalidated",
    "chrome-extension://",
    "moz-extension://",
    "safari-extension://",
    "Script error",
    "gtag",
    "googletagmanager",
    "analytics",
    "Uncaught (in promise)",
  ];

  const shouldSuppress = suppressedPromiseErrors.some(
    (errorPattern) =>
      errorMessage.toLowerCase().includes(errorPattern.toLowerCase()) ||
      errorString.toLowerCase().includes(errorPattern.toLowerCase())
  );

  if (shouldSuppress) {
    if (process.env.NODE_ENV === "development") {
      console.debug(
        "🔧 Suppressed unhandled promise rejection:",
        errorMessage || errorString
      );
    }
    event.preventDefault(); // Prevent the error from appearing in console
    return;
  }
});

// Suppress errors from browser extensions during user interactions
window.addEventListener("error", (event) => {
  const errorMessage = event.message || "";

  if (
    errorMessage.includes(
      "Could not establish connection. Receiving end does not exist"
    ) ||
    errorMessage.includes("Extension context invalidated") ||
    errorMessage.includes("The message port closed")
  ) {
    event.preventDefault(); // Prevent the error from appearing in console
  }
});

// Add global error handler for async errors
window.addEventListener("error", (event) => {
  const errorMessage = event.message || "";
  const filename = event.filename || "";

  const suppressedGlobalErrors = [
    "Could not establish connection. Receiving end does not exist",
    "Extension context invalidated",
    "The message port closed",
    "connection was closed",
    "connection is closed",
    "port is closed",
    "disconnected port",
    "invalidated context",
    "context was invalidated",
    "chrome-extension://",
    "moz-extension://",
    "safari-extension://",
    "Script error",
    "gtag",
    "googletagmanager",
    "analytics",
    "Uncaught (in promise)",
    // Additional Google Analytics specific errors
    "googletagmanager.com",
    "www.googletagmanager.com",
    "gtag/js",
    "dataLayer",
    "google-analytics",
    "ga.js",
    "analytics.js",
  ];

  const shouldSuppress = suppressedGlobalErrors.some(
    (errorPattern) =>
      errorMessage.toLowerCase().includes(errorPattern.toLowerCase()) ||
      filename.toLowerCase().includes(errorPattern.toLowerCase())
  );

  if (shouldSuppress) {
    if (process.env.NODE_ENV === "development") {
      console.debug(
        "🔧 Suppressed global error:",
        errorMessage,
        "in file:",
        filename
      );
    }
    event.preventDefault();
    return false;
  }

  // Also check the error object itself
  if (event.error && typeof event.error === "object") {
    const errorObjMessage = event.error.message || "";
    const errorObjStack = event.error.stack || "";
    const shouldSuppressObj = suppressedGlobalErrors.some(
      (errorPattern) =>
        errorObjMessage.toLowerCase().includes(errorPattern.toLowerCase()) ||
        errorObjStack.toLowerCase().includes(errorPattern.toLowerCase())
    );

    if (shouldSuppressObj) {
      if (process.env.NODE_ENV === "development") {
        console.debug("🔧 Suppressed error object:", errorObjMessage);
      }
      event.preventDefault();
      return false;
    }
  }
});

// Specific handler for Google Analytics errors
window.addEventListener("error", (event) => {
  if (event.filename && event.filename.includes("googletagmanager.com")) {
    if (process.env.NODE_ENV === "development") {
      console.debug("🔧 Suppressed Google Analytics error:", event.message);
    }
    event.preventDefault();
    return false;
  }
});

// Additional safety net for any remaining async errors
window.addEventListener("message", (event) => {
  // Suppress browser extension message errors
  try {
    // Let legitimate messages pass through
    if (event.source && event.data) {
      // This is a normal message, continue
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    if (
      errorMessage.includes("Could not establish connection") ||
      errorMessage.includes("Extension context") ||
      errorMessage.includes("Receiving end does not exist")
    ) {
      if (process.env.NODE_ENV === "development") {
        console.debug("🔧 Suppressed message error:", errorMessage);
      }
      event.stopImmediatePropagation();
    }
  }
});

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
